import { MouseEvent, useRef, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Customized,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { MicrobiomeAgeSample } from 'services/microbiome/types/microbiome-age.types';
import SampleDot from './components/SampleDot';
import SampleTooltip from './components/SampleTooltip';
import YourAgeLabel from './components/YourAgeLabel';
import useMicrobiomeAgeOverTimeModel from './hooks/useMicrobiomeAgeOverTimeModel';
import { interiorGridLines } from './interiorGridLines';
import styles from './MicrobiomeAgeOverTimeChart.module.css';

const LABEL_COLOR = 'var(--warm-grey-700)';

// Tier bands (each a [low, high] range) and the threshold lines dividing them,
// ordered from lowest microbiome age to highest.
const BANDS = [
  { dataKey: 'greenBand', fill: 'var(--green-100)' },
  { dataKey: 'greyBand', fill: 'var(--cold-grey-200)' },
  { dataKey: 'orangeBand', fill: 'var(--orange-100)' },
  { dataKey: 'redBand', fill: 'var(--red-100)' },
];
const THRESHOLD_LINES = [
  { dataKey: 'lower', stroke: 'var(--green-200)' },
  { dataKey: 'mid', stroke: 'var(--warm-grey-400)' },
  { dataKey: 'upper', stroke: 'var(--orange-200)' },
];

type Props = {
  metricId: number;
};

type TooltipState = {
  x: number;
  y: number;
  sample: MicrobiomeAgeSample | null;
};

export default function MicrobiomeAgeOverTimeChart({ metricId }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({
    x: 0,
    y: 0,
    sample: null,
  });

  const model = useMicrobiomeAgeOverTimeModel(metricId);

  if (!model) return null;

  const showTooltip = (
    sample: MicrobiomeAgeSample,
    e: MouseEvent<SVGElement>,
  ) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, sample });
  };
  const hideTooltip = () => setTooltip(prev => ({ ...prev, sample: null }));

  return (
    <div ref={containerRef} className={styles.wrapper}>
      <ResponsiveContainer width='100%' height={250}>
        <ComposedChart
          data={model.data}
          margin={{ top: 16, right: 16, bottom: 16, left: 0 }}
        >
          <CartesianGrid
            strokeDasharray='4 4'
            stroke='var(--warm-grey-600)'
            strokeOpacity={0.5}
            {...interiorGridLines(model.xTicks)}
          />

          {/* Evaluation tier bands (each is a [low, high] range). */}
          {BANDS.map(({ dataKey, fill }) => (
            <Area
              key={dataKey}
              dataKey={dataKey}
              type='linear'
              stroke='none'
              fill={fill}
              isAnimationActive={false}
              activeDot={false}
            />
          ))}

          {/* Threshold lines between the bands. */}
          {THRESHOLD_LINES.map(({ dataKey, stroke }) => (
            <Line
              key={dataKey}
              dataKey={dataKey}
              type='linear'
              stroke={stroke}
              strokeWidth={1.5}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          ))}

          {/* "Your age" reference: microbiome age == chronological age. */}
          <Line
            dataKey='diagonal'
            type='linear'
            stroke='var(--purple-300)'
            strokeWidth={1.5}
            strokeDasharray='4 4'
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />

          <XAxis
            dataKey='age'
            type='number'
            domain={model.xDomain}
            ticks={model.xTicks}
            allowDataOverflow
            tickLine={false}
            axisLine={{ stroke: 'var(--warm-grey-400)' }}
            tick={{ fill: LABEL_COLOR, fontSize: 11 }}
            height={36}
            label={{
              value: 'Chronological Age',
              position: 'insideBottom',
              offset: 0,
              fill: LABEL_COLOR,
              fontSize: 11,
            }}
          />
          <YAxis
            domain={model.yDomain}
            allowDataOverflow
            tickLine={false}
            axisLine={false}
            tick={{ fill: LABEL_COLOR, fontSize: 11 }}
            width={44}
            label={{
              value: 'Microbiome age',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              fill: LABEL_COLOR,
              fontSize: 11,
            }}
          />

          {/* Grey trend line connecting consecutive samples. */}
          {model.samples.slice(1).map((sample, i) => {
            const prev = model.samples[i];
            return (
              <ReferenceLine
                key={`connector-${sample.kitId}`}
                isFront
                ifOverflow='hidden'
                segment={[
                  { x: prev.age, y: prev.microbiomeAge },
                  { x: sample.age, y: sample.microbiomeAge },
                ]}
                stroke='var(--warm-grey-400)'
                strokeWidth={2}
              />
            );
          })}

          {/* Sample markers (tier-colored current, hollow past). */}
          {model.samples.map(sample => (
            <ReferenceDot
              key={`dot-${sample.kitId}`}
              x={sample.age}
              y={sample.microbiomeAge}
              r={0}
              isFront
              ifOverflow='hidden'
              shape={dotProps => (
                <SampleDot
                  {...dotProps}
                  sample={sample}
                  onShow={showTooltip}
                  onHide={hideTooltip}
                />
              )}
            />
          ))}
          {/* "your age" caption pinned to the diagonal's end. */}
          <Customized component={YourAgeLabel} />
        </ComposedChart>
      </ResponsiveContainer>

      <SampleTooltip x={tooltip.x} y={tooltip.y} sample={tooltip.sample} />
    </div>
  );
}
